<script setup lang="ts">
    // @ts-nocheck
    import WarningIcon from "@/assets/error_big.svg";
    import InfoIcon from "@/assets/info_dark.svg";
    import LinkIcon from "@/assets/link_external.svg";
    import ApprovalIcon from "@/assets/positive_notification.svg";
    import KButton from "@/components/Button.vue";

    /**
     * Används som en generell komponent för rendering av tag-lika element. ValtLakemedelInfoDisplay för exempel
     * (inom receptförskrivning)
     */

    const props = withDefaults(
        defineProps<{
            variant?: "default" | "none" | "approval" | "warning" | "info" | "link" | "tag";
            size?: "default" | "large" | "none";
            /**
             * Riktning i grid som content ska renderas. Ikonen kommer alltid renderas till vänster om allt content
             */
            contentDirection?: "horizontal" | "vertical";
            clearable?: boolean;
            clearPlacement?: "start" | "end";
            clickableArea?: "default" | "whole";
        }>(),
        {
            size: "default",
            variant: "default",
            contentDirection: "vertical",
            clearable: false,
            clearPlacement: "end",
            clickableArea: "default",
        }
    );

    const emit = defineEmits<{
        (event: "clear"): void;
    }>();

    const icons = {
        default: undefined,
        none: undefined,
        approval: ApprovalIcon,
        warning: WarningIcon,
        info: InfoIcon,
        link: LinkIcon,
    };

    const onClear = (event: Event, area: "default" | "whole") => {
        if (area !== "whole" || props.clickableArea === "whole") {
            emit("clear");
        }
    };
</script>

<template>
    <div
        :class="[
            'k-tag',
            `variant-${variant}`,
            `size-${size}`,
            contentDirection,
            { clickable: clickableArea === 'whole' },
        ]"
        @click="onClear($event, 'whole')"
        :role="clickableArea === 'whole' ? 'button' : 'generic'"
    >
        <div :class="['content-wrapper', `clear-placement-${clearPlacement}`]">
            <div v-if="$slots.icon || icons[variant]" class="icon">
                <slot name="icon">
                    <img v-if="icons[variant]" :src="icons[variant]" :alt="variant" />
                </slot>
            </div>
            <slot />
            <KButton v-if="clearable" class="clear" variant="none" size="none" @click="onClear">
                <img src="@/assets/cross_error_grey.svg" alt="Ta bort" />
            </KButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .k-tag {
        display: grid;
        align-items: center;
        grid-auto-flow: column;
        grid-gap: 0.4rem;
        border-radius: $border-radius-max;
        font-size: 0.875rem;
        line-height: 1.125rem;
        width: fit-content;
        min-height: fit-content;

        .content-wrapper {
            display: grid;
            grid-column-gap: $icon-gap-default;

            > * {
                grid-column: 2;
            }

            .icon {
                display: grid;
                place-items: center;
                grid-column: 1;
                grid-row: 1;
                place-self: center;

                img {
                    height: 16px;
                }
            }

            .clear {
                grid-column: 3;
                padding: 0;
            }

            &:not(:has(.icon)) {
                .icon {
                    display: none;
                }

                > * {
                    grid-column: 1;
                }

                .clear {
                    grid-column: 2;
                }
            }

            &.clear-placement-start {
                .icon {
                    grid-column: 2;
                }

                > * {
                    grid-column: 3;
                }

                .clear {
                    grid-column: 1;
                }

                &:not(:has(.icon)) {
                    .icon {
                        display: none;
                    }

                    > * {
                        grid-column: 2;
                    }

                    .clear {
                        grid-column: 1;
                    }
                }
            }
        }

        &.horizontal {
            .content-wrapper {
                grid-auto-flow: column;
            }
        }

        &.vertical {
            .content-wrapper {
                grid-auto-flow: row;
            }
        }

        &.variant-default {
            background-color: $color-background-tool-menu;
        }

        &.variant-approval {
            background-color: $color-approval;
        }

        &.variant-warning {
            background-color: $color-warning;
        }

        &.variant-info {
            background-color: $color-info;
        }

        &.variant-link {
            background-color: transparent;
        }

        &.variant-tag {
            background-color: $color-blue-light;
            border-radius: 20px;
        }

        &.size-default {
            height: $tag-height-default;
            padding: $tag-padding-default;
        }

        &.size-large {
            padding: 0.7rem;

            .icon {
                margin-right: 0.25rem;
            }
        }

        &.size-none {
            padding: 0;
        }

        &.clickable {
            cursor: pointer;
        }
    }
</style>
